import { getAuth } from "firebase/auth";
import { deleteDoc, doc, getDoc } from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import { DeletePostAlert } from "../components/DeletePostAlert";
import { useToast } from "../components/ui/use-toast";
import { Post } from "../models/Post";
import { db } from "../services/Firebase";
import { postConverter } from "../services/converters";
import { editorNavigationState } from "../lib/editorBackgroundRoute";

export function usePostActions() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const auth = getAuth();
  const currentUser = auth.currentUser;

  const handleEdit = (
    postId: string,
    options?: {
      openEditor?: boolean;
      intent?: "publish";
      /** Analytics only: which Publish button was used. */
      from?: "drafts_card" | "post_viewer";
    },
  ) => {
    const url = `/create-post?draftId=${postId}${
      options?.openEditor ? "&action=edit" : ""
    }${options?.intent ? `&intent=${options.intent}` : ""}${
      options?.from ? `&from=${options.from}` : ""
    }`;
    navigate(url, {
      state: editorNavigationState(location, false),
    });
  };

  const handleDelete = async (postId: string) => {
    const postRef = doc(db, "posts", postId).withConverter(postConverter);

    try {
      // Confirm the post still exists before deleting it.
      const postDoc = await getDoc(postRef);
      if (!postDoc.exists()) {
        throw new Error("Post not found");
      }
      // Delete the post document
      await deleteDoc(postRef);
      toast({
        title: "Post deleted",
        description: "Your post has been successfully deleted.",
      });
    } catch (error) {
      console.error("Error deleting post:", error);
      toast({
        title: "Error deleting post",
        description:
          error instanceof Error
            ? error.message
            : "There was a problem deleting your post. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDuplicate = async (postId: string) => {
    if (!currentUser) {
      toast({
        title: "Authentication Error",
        description: "You must be logged in to duplicate a post.",
        variant: "destructive",
      });
      return;
    }

    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/api/posts/${postId}/clone`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to duplicate post");
      }

      const result = await response.json();

      toast({
        title: "Post Duplicated",
        description: "A new draft has been created from the original post.",
      });

      // Navigate to the new draft
      navigate(`/create-post?draftId=${result.postId}`, {
        state: editorNavigationState(location, true),
      });
    } catch (error) {
      console.error("Error duplicating post:", error);
      toast({
        title: "Error Duplicating Post",
        description:
          error instanceof Error
            ? error.message
            : "An unknown error occurred while duplicating the post.",
        variant: "destructive",
      });
    }
  };

  const getDeleteDialog = (
    post: Post,
    hasPublished: boolean,
    trigger: React.ReactNode,
    onDelete?: () => void
  ): React.ReactNode => {
    return DeletePostAlert({
      post,
      hasPublished,
      trigger,
      onDelete: async () => {
        await handleDelete(post.id);
        onDelete?.();
      },
    });
  };

  return {
    handleEdit,
    handleDelete,
    handleDuplicate,
    getDeleteDialog,
  };
}
